import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import { apiRequest } from '../../../api/client';
import { DEVICE_TYPES, formatDeviceTypeLabel } from './deviceTypes';
import '../quotes/quote-generator.css';

type HistoryRow = {
  inspectionDate: string | null;
  technicianName: string;
  result: string;
  notes: string;
  checklistLabel?: string;
};

type DevicePayload = {
  _id: string;
  clientId?: { buildingName?: string; firstName?: string; lastName?: string };
  type: string;
  manufacturer?: string;
  modelName?: string;
  modelNumber?: string;
  serialNumber?: string;
  installationLocation?: string;
  floor?: string;
  zone?: string;
  installationDate?: string;
  lastInspectedAt?: string | null;
  lastInspectionResult?: string;
  status?: string;
  photos?: string[];
  notes?: string;
};

const emptyDraft = () => ({
  type: 'smoke_detector',
  manufacturer: '',
  modelName: '',
  modelNumber: '',
  serialNumber: '',
  installationLocation: '',
  floor: '',
  zone: '',
  installationDate: '',
  notes: ''
});

export default function DeviceDetailAdminPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [device, setDevice] = useState<DevicePayload | null>(null);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setErrorMessage(null);
    setLoading(true);
    try {
      const [dRes, hRes] = await Promise.all([
        apiRequest<{ success: boolean; data: DevicePayload }>(`/api/devices/${id}`),
        apiRequest<{ success: boolean; data: HistoryRow[] }>(`/api/devices/${id}/history`)
      ]);
      setDevice(dRes.data);
      setHistory(hRes.data || []);
    } catch (e: unknown) {
      setErrorMessage(e instanceof Error ? e.message : 'Could not load device.');
      setDevice(null);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  function openEdit() {
    if (!device) return;
    setDraft({
      type: device.type,
      manufacturer: device.manufacturer || '',
      modelName: device.modelName || '',
      modelNumber: device.modelNumber || '',
      serialNumber: device.serialNumber || '',
      installationLocation: device.installationLocation || '',
      floor: device.floor || '',
      zone: device.zone || '',
      installationDate: device.installationDate ? String(device.installationDate).slice(0, 10) : '',
      notes: device.notes || ''
    });
    setEditOpen(true);
  }

  async function saveEdit() {
    if (!id) return;
    setSaving(true);
    try {
      await apiRequest(`/api/devices/${id}`, {
        method: 'PATCH',
        json: {
          ...draft,
          installationDate: draft.installationDate || undefined
        }
      });
      setEditOpen(false);
      await load();
    } catch (e: unknown) {
      window.alert(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  function clientLabel(cl?: DevicePayload['clientId']): string {
    if (!cl) return '—';
    const n = `${cl.firstName || ''} ${cl.lastName || ''}`.trim();
    return n || cl.buildingName || 'Client';
  }

  if (!id) {
    return (
      <AppShell>
        <div className="quote-page" />
      </AppShell>
    );
  }

  if (loading) {
    return (
      <AppShell>
        <div className="quote-page">
          <div className="quote-container"><p style={{ color: 'var(--dash-muted)' }}>Loading…</p></div>
        </div>
      </AppShell>
    );
  }

  if (!device || errorMessage) {
    return (
      <AppShell>
        <div className="quote-page">
          <div className="quote-container">
            <div className="banner banner-error">{errorMessage || 'Device not found.'}</div>
            <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  function resultTone(r?: string): string {
    if (r === 'pass') return '#2e8a57';
    if (r === 'fail') return '#c0392b';
    return '#64748b';
  }

  return (
    <AppShell>
      <div className="quote-page">
        <div className="quote-container" style={{ maxWidth: 960 }}>
          <header style={{ marginBottom: 22 }}>
            <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
              ← Back
            </button>
            <h1 style={{ fontSize: '2rem', marginTop: 12 }}>{formatDeviceTypeLabel(device.type)}</h1>
            <p style={{ color: 'var(--dash-muted)', margin: 0 }}>
              {device.installationLocation || 'Registered asset'} • {clientLabel(device.clientId)}
            </p>
            <button type="button" className="btn-primary" style={{ marginTop: 14 }} onClick={openEdit}>
              Edit details
            </button>
          </header>

          <section className="quote-card" style={{ marginBottom: 18 }}>
            <div className="quote-section-title">Specifications</div>
            <dl style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 8, columnGap: 12 }}>
              <dt style={{ color: 'var(--dash-muted)', fontWeight: 600 }}>Manufacturer</dt>
              <dd style={{ margin: 0 }}>{device.manufacturer || '—'}</dd>
              <dt style={{ color: 'var(--dash-muted)', fontWeight: 600 }}>Model</dt>
              <dd style={{ margin: 0 }}>{[device.modelName, device.modelNumber].filter(Boolean).join(' · ') || '—'}</dd>
              <dt style={{ color: 'var(--dash-muted)', fontWeight: 600 }}>Serial</dt>
              <dd style={{ margin: 0 }}>{device.serialNumber || '—'}</dd>
              <dt style={{ color: 'var(--dash-muted)', fontWeight: 600 }}>Floor / zone</dt>
              <dd style={{ margin: 0 }}>{[device.floor, device.zone].filter(Boolean).join(' · ') || '—'}</dd>
              <dt style={{ color: 'var(--dash-muted)', fontWeight: 600 }}>Installation date</dt>
              <dd style={{ margin: 0 }}>{device.installationDate ? new Date(device.installationDate).toLocaleDateString() : '—'}</dd>
              <dt style={{ color: 'var(--dash-muted)', fontWeight: 600 }}>Last inspection</dt>
              <dd style={{ margin: 0 }}>
                {device.lastInspectedAt ? new Date(device.lastInspectedAt).toLocaleDateString() : '—'}{' '}
                <span style={{ marginLeft: 8, fontWeight: 800, color: resultTone(device.lastInspectionResult) }}>
                  {device.lastInspectionResult?.replace(/_/g, ' ') || 'not tested'}
                </span>
              </dd>
              <dt style={{ color: 'var(--dash-muted)', fontWeight: 600 }}>Status</dt>
              <dd style={{ margin: 0 }}>{device.status}</dd>
              <dt style={{ color: 'var(--dash-muted)', fontWeight: 600 }}>Notes</dt>
              <dd style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{device.notes || '—'}</dd>
            </dl>
          </section>

          {device.photos && device.photos.length > 0 ? (
            <section className="quote-card" style={{ marginBottom: 18 }}>
              <div className="quote-section-title">Photos</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {device.photos.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                    <img src={url} alt="" style={{ width: 140, height: 100, objectFit: 'cover', borderRadius: 8 }} />
                  </a>
                ))}
              </div>
            </section>
          ) : null}

          <section className="quote-card">
            <div className="quote-section-title">Inspection history</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid rgba(0,0,0,0.06)', color: 'var(--dash-muted)', textAlign: 'left' }}>
                    <th style={{ padding: '8px' }}>Date</th>
                    <th style={{ padding: '8px' }}>Technician</th>
                    <th style={{ padding: '8px' }}>Result</th>
                    <th style={{ padding: '8px' }}>Checklist item</th>
                    <th style={{ padding: '8px' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ padding: 16, color: 'var(--dash-muted)', textAlign: 'center' }}>
                        No inspection history tied to this device yet.
                      </td>
                    </tr>
                  ) : (
                    history.map((row, idx) => (
                      <tr key={`${String(row.inspectionDate)}-${idx}`} style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                        <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                          {row.inspectionDate ? new Date(row.inspectionDate).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ padding: '8px' }}>{row.technicianName}</td>
                        <td style={{ padding: '8px', fontWeight: 800, color: resultTone(row.result) }}>{row.result}</td>
                        <td style={{ padding: '8px' }}>{row.checklistLabel || '—'}</td>
                        <td style={{ padding: '8px' }}>{row.notes || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {editOpen && (
          <div
            role="presentation"
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22 }}
          >
            <div className="quote-card" style={{ maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 22 }}>
              <div className="quote-section-title" style={{ marginBottom: 12 }}>
                Update device
              </div>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label className="form-label">Type</label>
                  <select className="form-input" value={draft.type} onChange={(e) => setDraft((p) => ({ ...p, type: e.target.value }))}>
                    {DEVICE_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {formatDeviceTypeLabel(t)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Manufacturer</label>
                  <input className="form-input" value={draft.manufacturer} onChange={(e) => setDraft((p) => ({ ...p, manufacturer: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Model name</label>
                  <input className="form-input" value={draft.modelName} onChange={(e) => setDraft((p) => ({ ...p, modelName: e.target.value }))} />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Model number</label>
                  <input className="form-input" value={draft.modelNumber} onChange={(e) => setDraft((p) => ({ ...p, modelNumber: e.target.value }))} />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Serial number</label>
                  <input className="form-input" value={draft.serialNumber} onChange={(e) => setDraft((p) => ({ ...p, serialNumber: e.target.value }))} />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Installation location</label>
                  <input className="form-input" value={draft.installationLocation} onChange={(e) => setDraft((p) => ({ ...p, installationLocation: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Floor</label>
                  <input className="form-input" value={draft.floor} onChange={(e) => setDraft((p) => ({ ...p, floor: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Zone</label>
                  <input className="form-input" value={draft.zone} onChange={(e) => setDraft((p) => ({ ...p, zone: e.target.value }))} />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Installation date</label>
                  <input className="form-input" type="date" value={draft.installationDate} onChange={(e) => setDraft((p) => ({ ...p, installationDate: e.target.value }))} />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" rows={3} value={draft.notes} onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value }))} />
                </div>
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn-secondary" disabled={saving} onClick={() => setEditOpen(false)}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" disabled={saving} onClick={() => saveEdit().catch(() => undefined)}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
